import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

const VARIANTS: Record<AlertVariant, { classes: string; icon: IconName }> = {
  info: {
    classes: 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200',
    icon: 'info',
  },
  success: {
    classes: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    icon: 'check-circle',
  },
  warning: {
    classes: 'bg-bsite-100 text-bsite-900 dark:bg-bsite-950 dark:text-bsite-100',
    icon: 'warning',
  },
  error: {
    classes: 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
    icon: 'alert-circle',
  },
};

/**
 * Avviso. Errori e avvertenze usano `role="alert"` (annunciati subito), informazioni ed esiti
 * positivi `role="status"` (annunciati senza interrompere).
 */
@Component({
  selector: 'bs-alert',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
  template: `
    <div [class]="classes()" [attr.role]="role()" [attr.data-testid]="testId()">
      <bs-icon [name]="icon()" [size]="20" />
      <div class="flex min-w-0 flex-col gap-1">
        @if (heading(); as text) {
          <p class="font-semibold">{{ text }}</p>
        }
        <div><ng-content /></div>
      </div>
    </div>
  `,
})
export class AlertComponent {
  readonly variant = input<AlertVariant>('info');
  readonly heading = input<string | null>(null);
  readonly testId = input<string | null>(null);

  protected readonly role = computed(() =>
    this.variant() === 'error' || this.variant() === 'warning' ? 'alert' : 'status',
  );
  protected readonly icon = computed(() => VARIANTS[this.variant()].icon);
  protected readonly classes = computed(
    () =>
      `flex items-start gap-3 rounded-2xl p-4 text-sm ring-1 ring-inset ring-current/15 ${VARIANTS[this.variant()].classes}`,
  );
}
